/**
 * @title Dialog Overview
 */
import { AfterViewInit, Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { BehaviorSubject, debounceTime, of, Subject, Subscription, switchMap, tap, throttleTime } from "rxjs";
import { MatTableDataSource } from "@angular/material/table";
import { filter, map } from "rxjs/operators";
import { autoMonitorPageView } from "src/app/core/decorators/auto-monitor-page-view.decorator";
import { MonitoringService } from "src/app/core/services/monitoring.service";
import { ModelDownloadTaskInProgress, ModelDownloadTaskInProgressService } from "src/app/core/services/model-download-task-in-progress.service";
import { CollectionFilesDownloaderService } from "src/app/core/api/collection-files-downloader.service";



@Component({
  standalone: false,
  selector: 'app-download-tasks-table-dialog',
  templateUrl: './download-tasks-table-dialog.html',
  styleUrls: ['./download-tasks-table-dialog.component.scss']
})
@autoMonitorPageView({ name: 'Download Tasks In Progress Dialog', trackOnInitToAfterInit: false })
export class DownloadTasksTableDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  private tasksInProgressService$ = Subscription.EMPTY;
  private dataSourceRefresh$ = new Subject<void>();
  private dataSourceRefreshSub$ = Subscription.EMPTY;
  private isRetryingSub$ = Subscription.EMPTY;

  rootTaskInProgress: ModelDownloadTaskInProgress;
  tasksInProgress: ModelDownloadTaskInProgress[];
  displayedColumns: string[] = ['title', 'progress', 'status'];
  dataSource = new MatTableDataSource([]);
  filterText: string = '';
  lastSearchedText: string;
  isRetrying = false;

  private inputChange = new BehaviorSubject(false);
  private inputChange$: Subscription;

  constructor(
    public dialogRef: MatDialogRef<DownloadTasksTableDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: string,
    private modelDownloadTaskInProgressService: ModelDownloadTaskInProgressService,
    private collectionFilesDownloaderService: CollectionFilesDownloaderService,
    private monitoringService: MonitoringService
  ) {
    this.inputChange$ = this.inputChange.pipe(debounceTime(100), tap(inputRecd => {
      if (inputRecd) {
        const filterValue = this.filterText || '';
        this.lastSearchedText = filterValue;
        this.dataSource.filter = filterValue.trim().toLowerCase();
      }
    })).subscribe();
  }
  ngAfterViewInit(): void {
    // do nothing
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    console.log('ngOnDestroy of DownloadTasksTableDialogComponent');
    this.dataSourceRefreshSub$?.unsubscribe();
    this.isRetryingSub$?.unsubscribe();
  }

  ngOnInit(): void {
    if (this.data) {
      this.rootTaskInProgress = this.modelDownloadTaskInProgressService.tasksInProgress.find(p => p.Id === this.data && p.RootId === this.data);
      this.tasksInProgress = this.modelDownloadTaskInProgressService.tasksInProgress.filter(p => p.Id !== this.data && p.RootId === this.data);
      this.dataSource.data = this.tasksInProgress;

      // Debounce rapid-fire dataSource updates (e.g. when the file list arrives all at once)
      this.dataSourceRefreshSub$ = this.dataSourceRefresh$.pipe(debounceTime(0)).subscribe(() => {
        this.dataSource.data = this.tasksInProgress.filter(p => p.Id !== this.data && p.RootId === this.data);
      });

      this.tasksInProgressService$ = this.modelDownloadTaskInProgressService.taskInProgress$
        .pipe(
          filter(p => p.RootId === this.data),
          tap(p => {
            if (p.Id === this.data) {
              if (!this.rootTaskInProgress) {
                this.rootTaskInProgress = p;
              } else {
                this.rootTaskInProgress.Progress = p.Progress;
                this.rootTaskInProgress.Status = p.Status;
              }
            } else {
              this.modelDownloadTaskInProgressService.updateTaskProgress(p, this.tasksInProgress);
            }
            this.dataSourceRefresh$.next();
            if (!this.filterText.trim() && p.Status === ModelDownloadTaskInProgressService.TaskFailureStatus) {
              this.filterText = 'fail';
            }
          }))
        .subscribe();
        this.inputChange.next(true);

      this.isRetryingSub$ = this.collectionFilesDownloaderService.isRetrying$(this.data)
        .subscribe(retrying => { this.isRetrying = retrying; });
    }
  }

  applyFilter(event: KeyboardEvent) {
    this.inputChange.next(true);
  }

  // ---- Summary helpers -------------------------------------------------------

  get failedTasks(): ModelDownloadTaskInProgress[] {
    return (this.tasksInProgress ?? []).filter(
      t => t.Status === ModelDownloadTaskInProgressService.TaskFailureStatus
    );
  }

  get successCount(): number {
    return (this.tasksInProgress ?? []).filter(
      t => t.Status === ModelDownloadTaskInProgressService.TaskSuccessStatus
    ).length;
  }

  get totalFileCount(): number {
    return (this.tasksInProgress ?? []).length;
  }

  get canRetry(): boolean {
    return this.failedTasks.length > 0 && !this.isRetrying;
  }

  // ---- Retry actions ---------------------------------------------------------

  retryFailedDownloads(): void {
    this.collectionFilesDownloaderService.retryFailedDownloads(this.data);
  }

  retryFile(fileId: string): void {
    this.collectionFilesDownloaderService.retryFile(this.data, fileId);
  }

  // ---- Display helpers -------------------------------------------------------

  getBackgroundColor(taskInProgress: ModelDownloadTaskInProgress): string {
    switch (taskInProgress.Status) {
      // case 'NOT_STARTED':
      //   return '#f1f0f0';
      case ModelDownloadTaskInProgressService.TaskInProgressStatus:
        return '#ffffff';
      case ModelDownloadTaskInProgressService.TaskSuccessStatus:
        return '#d9e8c0';
      case ModelDownloadTaskInProgressService.TaskFailureStatus:
        return '#f3c7cb';
    }

  }

  getBackgroundPadding(taskInProgress: ModelDownloadTaskInProgress): number {
    if (taskInProgress.Status === ModelDownloadTaskInProgressService.TaskSuccessStatus || taskInProgress.Status === ModelDownloadTaskInProgressService.TaskFailureStatus)
      return 0;
    else
      return 7;
  }

  getTitleFontSize(taskInProgress: ModelDownloadTaskInProgress): string {
    return taskInProgress.Progress < 100 ? "20" : "14";
  }
}

